import { Button } from "@design-system/Inputs/Button";
import { Box } from "@design-system/Layout/Box";
import { FlexRow } from "@design-system/Layout/Flex";
import { type FC, type PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import "../i18n";
import { useAtomValue } from "jotai";
import { currentUserLocaleAtom } from "@app/store/userStore";

type LaunchTutorialButtonProps = {
    /** This informatio is added to the data-chameleon
     * atrribute of the button; it'll automatically be
     * prefixed with "launch-tutorial-"
     *
     * @example
     * // If you pass "my-page" as chameleonData prop:
     * // The button will have the following attribute:
     * // <button data-chameleon="launch-tutorial-my-page" />
     */
    chameleonData: string;
    /**
     * The id of the intercom tour to launch
     */
    intercomTourId?: { en: number; fr: number };
};

export const LaunchTutorialButton: FC<LaunchTutorialButtonProps> = ({
    chameleonData,
    intercomTourId,
}) => {
    const { t } = useTranslation("Chameleon");
    const locale = useAtomValue(currentUserLocaleAtom);

    const handleClick = () => {
        if (intercomTourId && window.Intercom && locale) {
            window.Intercom("startTour", intercomTourId[locale]);
        }
    };

    return (
        <Button
            id={`launch-tutorial-${chameleonData}-${locale ?? "fr"}`}
            data-chameleon={`launch-tutorial-${chameleonData}`}
            variant="tonal"
            size="md"
            onClick={handleClick}
        >
            {t("launchTutorialButton")}
            <Button.Icon name="circleInfo" />
        </Button>
    );
};

export const WrapperWithLaunchTutorialButton: FC<
    PropsWithChildren<LaunchTutorialButtonProps>
> = ({ children, chameleonData, intercomTourId }) => {
    return (
        <FlexRow alignItems="center" gap="3">
            <LaunchTutorialButton
                chameleonData={chameleonData}
                intercomTourId={intercomTourId}
            />
            {children && (
                <>
                    <Box className="border-l border-secondary" h="5" mx="1" />
                    <Box>{children}</Box>
                </>
            )}
        </FlexRow>
    );
};
